import {
    Custom_Upgrade_Validation_Failure_Codes_Enum,
    Custom_Upgrade_Validation_Failure_Severities_Enum,
    Custom_Upgrade_Validation_Statuses_Enum,
    In_Queue_Model_Variants_Enum
} from "generated/graphql";
import {
    Cluster,
    ClusterModelSet,
    ClusterStatus,
    ScenarioStage
} from "in_queue/types/clusterType";
import { InQueueSubscription } from "in_queue/types/projectType";
import { clusterStatusFromSubscription } from "./clusterStatus";

const DECISION_POINT_STATUSES: ReadonlySet<ClusterStatus> = new Set([
    ClusterStatus.DpMode,
    ClusterStatus.OnlyDpMode,
    ClusterStatus.AfterDp
]);

// One failure emitted by a validation, for a single model variant. Collapsed
// into a display-ready `ValidationFailure` by `dedupeValidationFailures`.
export interface RawValidationFailure {
    readonly severity: Custom_Upgrade_Validation_Failure_Severities_Enum;
    readonly code: Custom_Upgrade_Validation_Failure_Codes_Enum;
    readonly message: string;
    readonly lineNumber: number | null;
    readonly offendingText: string | null;
    readonly suggestedFix: string | null;
    readonly warnId: string | null;
    readonly variant: In_Queue_Model_Variants_Enum;
    readonly variantDescription: string;
}

export interface ValidationFailure {
    readonly severity: Custom_Upgrade_Validation_Failure_Severities_Enum;
    readonly code: Custom_Upgrade_Validation_Failure_Codes_Enum;
    readonly codeDescription: string;
    readonly message: string;
    /** Variants this failure applies to; empty when it applies to every variant. */
    readonly variantDescriptions: ReadonlyArray<string>;
}

export interface ModelSetValidation {
    readonly clusterModelSetId: string;
    readonly status: Custom_Upgrade_Validation_Statuses_Enum;
    readonly errorMessage: string | null;
    readonly submittedAt: Date | null;
    readonly completedAt: Date | null;
    readonly failures: ReadonlyArray<ValidationFailure>;
}

// A failure is the "same" across variants when everything but the variant
// matches. Grouped failures collapse to one row; their variants are listed
// (or omitted when the failure spans every variant of the upgrade).
const failureGroupKey = (failure: RawValidationFailure): string =>
    JSON.stringify([
        failure.severity,
        failure.code,
        failure.message,
        failure.lineNumber,
        failure.offendingText,
        failure.suggestedFix,
        failure.warnId
    ]);

export const dedupeValidationFailures = (
    rawFailures: ReadonlyArray<RawValidationFailure>,
    upgradeVariants: ReadonlySet<In_Queue_Model_Variants_Enum>,
    codeDescriptions: ReadonlyMap<string, string>
): ReadonlyArray<ValidationFailure> => {
    const groups = new Map<
        string,
        {
            readonly failure: RawValidationFailure;
            readonly variants: Map<In_Queue_Model_Variants_Enum, string>;
        }
    >();

    rawFailures.forEach((failure) => {
        const key = failureGroupKey(failure);
        const group = groups.get(key);
        if (group) {
            group.variants.set(failure.variant, failure.variantDescription);
        } else {
            groups.set(key, {
                failure,
                variants: new Map([
                    [failure.variant, failure.variantDescription]
                ])
            });
        }
    });

    // Fall back to the variants seen in the failures when the upgrade exposes
    // none (e.g. a non-model-specific upgrade), so a single-variant failure
    // still reads as "all variants".
    const universe =
        upgradeVariants.size > 0
            ? upgradeVariants
            : new Set(rawFailures.map((failure) => failure.variant));

    return Array.from(groups.values()).map(({ failure, variants }) => {
        const spansAllVariants =
            universe.size > 0 &&
            Array.from(universe).every((variant) => variants.has(variant));
        return {
            severity: failure.severity,
            code: failure.code,
            codeDescription: codeDescriptions.get(failure.code) ?? "",
            message: failure.message,
            variantDescriptions: spansAllVariants
                ? []
                : Array.from(variants.values()).sort()
        };
    });
};

export type ValidationState =
    | "usable"
    | "failed"
    | "validating"
    | "stale"
    | "noModelSet";

export interface ValidationStatus {
    readonly state: ValidationState;
    readonly errorMessage: string | null;
}

export const VALIDATION_STATE_LABEL: Record<ValidationState, string> = {
    usable: "Validated",
    failed: "Failed",
    validating: "Validating…",
    stale: "Stale",
    noModelSet: "No model set"
};

// Why applying an upgrade in this state is risky. Each entry continues a
// sentence that opens with the quoted upgrade name.
export const VALIDATION_RISK_REASON: Record<
    Exclude<ValidationState, "usable">,
    string
> = {
    failed: "failed validation against the current model set. This scenario is likely to fail.",
    validating:
        "is still being validated against the current model set. This scenario may fail.",
    stale: "hasn't been validated against the current model set. This scenario may fail.",
    noModelSet:
        "has no current model set to validate against. This scenario may fail."
};

export const VALIDATION_STATE_TOOLTIP: Record<ValidationState, string> = {
    usable: "This upgrade is validated against the current model set.",
    failed: "This upgrade failed validation against the current model set.",
    validating:
        "This upgrade is being validated against the current model set.",
    stale: "This upgrade hasn't been validated against the current model set.",
    noModelSet: "There's no current model set to validate against."
};

export const getCurrentModelSetForStage = (
    cluster: Cluster,
    stage: ScenarioStage
): ClusterModelSet | null =>
    stage === "DECISION_POINT"
        ? cluster.decisionPointModelSets.currentModelSet
        : cluster.previewModelSets.currentModelSet;

// A cluster is only ever runnable in one stage at a time (see
// in_queue_cluster_statuses), so its active stage is derived from its status —
// which a subscription may override on a per-cluster basis. The Hasura function
// custom_upgrade_current_validation repeats this mapping, so change both together.
export const getActiveScenarioStage = (
    cluster: Cluster,
    subscription?: InQueueSubscription
): ScenarioStage =>
    DECISION_POINT_STATUSES.has(
        clusterStatusFromSubscription(cluster, subscription)
    )
        ? "DECISION_POINT"
        : "PREVIEW";

export const getRelevantModelSet = (
    cluster: Cluster,
    subscription?: InQueueSubscription
): ClusterModelSet | null =>
    getCurrentModelSetForStage(
        cluster,
        getActiveScenarioStage(cluster, subscription)
    );

const validationTime = (validation: ModelSetValidation): number => {
    const date = validation.completedAt ?? validation.submittedAt;
    return date === null ? 0 : date.getTime();
};

// The latest validation across any model set, by completion (falling back to
// submission) time. Useful for surfacing "last validated …" when the current
// model set is stale.
export const getMostRecentModelSetValidation = (
    validations: ReadonlyArray<ModelSetValidation>
): ModelSetValidation | null =>
    validations.reduce<ModelSetValidation | null>(
        (latest, validation) =>
            latest === null ||
            validationTime(validation) > validationTime(latest)
                ? validation
                : latest,
        null
    );

// `current` is the upgrade's Hasura `current_validation`, which picks the
// validation for the relevant model set.
export const getUpgradeValidationState = (
    current: ModelSetValidation | null,
    target: ClusterModelSet | null
): ValidationStatus => {
    if (!target) {
        return { state: "noModelSet", errorMessage: null };
    }

    if (current === null) {
        return { state: "stale", errorMessage: null };
    }

    switch (current.status) {
        case Custom_Upgrade_Validation_Statuses_Enum.Succeeded:
            return { state: "usable", errorMessage: null };
        case Custom_Upgrade_Validation_Statuses_Enum.Failed:
            return { state: "failed", errorMessage: current.errorMessage };
        case Custom_Upgrade_Validation_Statuses_Enum.Requested:
        case Custom_Upgrade_Validation_Statuses_Enum.Running:
            return { state: "validating", errorMessage: null };
    }
};

export interface ModelSetValidationInfo {
    readonly all: ReadonlyArray<ModelSetValidation>;
    readonly current: ModelSetValidation | null;
    readonly mostRecent: ModelSetValidation | null;
    readonly status: ValidationStatus;
}

export const getModelSetValidationInfo = (
    validations: ReadonlyArray<ModelSetValidation>,
    current: ModelSetValidation | null,
    target: ClusterModelSet | null
): ModelSetValidationInfo => ({
    all: validations,
    current,
    mostRecent: getMostRecentModelSetValidation(validations),
    status: getUpgradeValidationState(current, target)
});
